import React from 'react';
import { RefreshCcw, XCircle, CheckCircle, Clock, CreditCard, AlertTriangle } from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const CancellationsAndRefunds = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <RefreshCcw className="w-4 h-4" />
                <span className="text-sm font-medium">Cancellation & Refund Policy</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Cancellations & Refunds
              </h1>
              <p className="text-lg md:text-xl text-orange-100 max-w-2xl mx-auto">
                Clear policies for cancellations, refunds, and replacements
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            <p className="text-gray-600 text-sm">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <p className="text-gray-700 leading-relaxed">
              At <strong>HomeCure</strong>, a brand of <strong>Sanghmitra Healthcare Private Limited</strong>, we understand that plans may change. This Cancellations and Refunds Policy outlines the terms and conditions for canceling bookings and requesting refunds or replacements for our services and products.
            </p>

            {/* Section 1 - Service Cancellations */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. CANCELLATION POLICY BY SERVICE TYPE</h2>
              </div>
              <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">1.1 Doctor Consultations & Online Consultations</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Cancellation:</strong> Not allowed - appointments cannot be canceled</li>
                    <li><strong>Rescheduling:</strong> Allowed - you can reschedule your appointment based on doctor availability</li>
                    <li><strong>Refund:</strong> No refunds available for appointments or online consultations</li>
                    <li><strong>No-show:</strong> No refund, consultation fee forfeited</li>
                    <li><strong>Important:</strong> Please reschedule in advance if you need to change your appointment time</li>
                  </ul>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">1.2 Medical Tests & Test Packages</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Cancellation allowed:</strong> Before our representative arrives for sample collection</li>
                    <li><strong>Rescheduling:</strong> Allowed - you can reschedule your appointment for sample collection</li>
                    <li><strong>Refund:</strong> 100% refund if canceled before representative arrival</li>
                    <li><strong>Refund Timeline:</strong> Amount will be refunded within 72 working hours of cancellation</li>
                    <li><strong>After representative arrival:</strong> No cancellation or refund allowed</li>
                    <li><strong>After sample collection:</strong> No cancellation or refund allowed</li>
                    <li><strong>No-show:</strong> No refund if you are unavailable at the scheduled time</li>
                  </ul>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">1.3 Paramedical Services (Nursing, Physiotherapy, Elderly Care, etc.)</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Cancellation:</strong> Not allowed - appointments cannot be canceled</li>
                    <li><strong>Rescheduling:</strong> Allowed - you can reschedule your appointment based on service provider availability</li>
                    <li><strong>Refund:</strong> No refunds available for paramedical services</li>
                    <li><strong>No-show:</strong> No refund, service fee forfeited</li>
                    <li><strong>Multi-session packages:</strong> Rescheduling available for individual sessions, no cancellation or refund</li>
                    <li><strong>Important:</strong> Please reschedule in advance if you need to change your service time</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                  <p className="font-bold text-gray-900 mb-2">1.4 Pharmacy Orders (Medicines & Products)</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Cancellation allowed:</strong> Before order dispatch</li>
                    <li><strong>Refund for cancellation:</strong> 100% if canceled before dispatch</li>
                    <li><strong>After dispatch:</strong> No cancellation allowed</li>
                    <li><strong>No refunds after delivery:</strong> Medicines and products are non-refundable once delivered</li>
                    <li><strong>Replacement only:</strong> Eligible only for wrong product, defective items, or quality issues (see Section 4)</li>
                    <li><strong>Important:</strong> Please review your order carefully before confirming, as no refunds are provided for delivered orders</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2 - Rescheduling */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. RESCHEDULING POLICY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>2.1 Eligibility</strong></p>
                <p>Rescheduling is allowed for most services, subject to availability.</p>

                <p><strong>2.2 Rescheduling Terms</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>First rescheduling:</strong> Free of charge (subject to provider availability)</li>
                  <li><strong>Subsequent rescheduling:</strong> Nominal fee may apply (₹50-100)</li>
                  <li><strong>Time limit:</strong> Must be rescheduled before the original cancellation deadline</li>
                </ul>

                <p><strong>2.3 How to Reschedule</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Go to "My Bookings" in the app/website</li>
                  <li>Select the booking and click "Reschedule"</li>
                  <li>Choose a new time slot</li>
                  <li>Confirm rescheduling</li>
                </ul>
              </div>
            </section>

            {/* Section 3 - Refund Process */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. REFUND PROCESS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>3.1 Refund Eligibility</strong></p>
                <p>Refunds are processed in the following scenarios:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>User cancellation within allowed time frame</li>
                  <li>Service provider cancellation</li>
                  <li>Service not delivered as booked</li>
                  <li>Duplicate payment or overcharge</li>
                  <li>Quality issues with delivered products (replacement preferred)</li>
                </ul>

                <p><strong>3.2 Refund Timeline</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medical Tests & Packages:</strong> Refund processed within 72 working hours of cancellation</li>
                  <li><strong>Pre-dispatch Cancellations (Medicines):</strong> Within 5-7 business days</li>
                  <li><strong>Credit to account:</strong> 5-10 business days (depending on bank/payment method)</li>
                  <li><strong>Note:</strong> Actual credit time may vary based on your bank's processing time</li>
                </ul>

                <p><strong>3.3 Refund Method</strong></p>
                <p>Refunds are credited to the original payment method:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medical Tests & Packages:</strong> Refund initiated within 72 working hours. Credit to your account in 5-10 business days.</li>
                  <li><strong>Pre-dispatch Medicine Cancellations:</strong> Refund initiated within 5-7 business days. Credit to your account in 5-10 business days.</li>
                  <li><strong>Credit/Debit Card:</strong> May take 5-10 business days from refund initiation</li>
                  <li><strong>UPI/Net Banking:</strong> May take 3-7 business days from refund initiation</li>
                  <li><strong>Wallet:</strong> May take 2-5 business days from refund initiation</li>
                  <li><strong>Cash on Delivery:</strong> Bank transfer (provide account details) - 5-10 business days</li>
                  <li><strong>Note:</strong> Actual credit time depends on your bank or payment provider's processing time</li>
                </ul>

                <p><strong>3.4 How to Request a Refund</strong></p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Go to "My Orders" or "My Bookings"</li>
                  <li>Select the relevant order/booking</li>
                  <li>Click "Request Refund" or "Cancel Order"</li>
                  <li>Provide reason for refund (if applicable)</li>
                  <li>Submit request</li>
                  <li>Our team will review and process within 3-5 business days</li>
                </ol>
              </div>
            </section>

            {/* Section 4 - Replacement Policy */}
            <section>
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">4. REPLACEMENT POLICY (MEDICINES & PRODUCTS)</h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      <strong>Important:</strong> As per healthcare and pharmaceutical regulations, <strong>we do not accept returns for medicines and medical products</strong> once delivered. However, we offer <strong>replacements</strong> in the following cases:
                    </p>

                    <div className="space-y-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">4.1 Replacement Eligibility</p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                          <li><strong>Wrong item delivered:</strong> If you received a different product than ordered</li>
                          <li><strong>Damaged/Defective product:</strong> If the product arrived damaged, broken, or defective</li>
                          <li><strong>Expired or near-expiry products:</strong> If products have less than 6 months validity from delivery date</li>
                          <li><strong>Quality issues:</strong> Manufacturing defects, tampered packaging, or incorrect quantity</li>
                          <li><strong>Missing items:</strong> If part of your order is missing</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-bold text-gray-900 mb-2">4.2 Replacement Process</p>
                        <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                          <li><strong>Report within 48 hours:</strong> Contact customer support within 48 hours of delivery</li>
                          <li><strong>Provide proof:</strong> Share clear photos/videos of the product, packaging, and issue</li>
                          <li><strong>Verification:</strong> Our team will review your request within 24-48 hours</li>
                          <li><strong>Approval:</strong> If approved, replacement will be arranged at no additional cost</li>
                          <li><strong>Collection & Delivery:</strong> Defective product may be collected, and replacement delivered within 2-5 business days</li>
                        </ol>
                      </div>

                      <div>
                        <p className="font-bold text-gray-900 mb-2">4.3 Non-Replaceable Items</p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700">
                          <li>Products used, opened, or consumed</li>
                          <li>Products with intact seals (unless quality issue)</li>
                          <li>Products ordered by mistake (user error)</li>
                          <li>Products damaged due to improper storage or handling by user</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-bold text-gray-900 mb-2">4.4 Alternative: Refund for Replacement Cases</p>
                        <p className="text-gray-700">
                          If replacement is not possible (product out of stock, discontinued, etc.), a full refund will be processed to your original payment method.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 - Provider Cancellation */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <RefreshCcw className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. CANCELLATION BY SERVICE PROVIDER</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>5.1 Provider-Initiated Cancellations</strong></p>
                <p>If a service provider cancels your booking, you will be:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Notified immediately via SMS/email/app</li>
                  <li>Offered an alternative provider or time slot</li>
                  <li>Entitled to a 100% refund if you do not wish to reschedule</li>
                </ul>

                <p><strong>5.2 Emergency/Unavoidable Cancellations</strong></p>
                <p>In case of emergencies, natural disasters, or unforeseen circumstances, we will make every effort to notify you and arrange alternatives or refunds.</p>
              </div>
            </section>

            {/* Section 6 - Non-Refundable Scenarios */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. NON-REFUNDABLE SCENARIOS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>Refunds are <strong>not applicable</strong> in the following cases:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Services already completed or delivered</li>
                  <li>User no-show without prior cancellation</li>
                  <li>Late cancellations beyond the allowed time frame</li>
                  <li>Products ordered by mistake (unless replacement-eligible)</li>
                  <li>Change of mind after service completion or product delivery</li>
                  <li>User-provided incorrect information leading to service issues</li>
                </ul>
              </div>
            </section>

            {/* Section 7 - Partial Refunds */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. PARTIAL REFUNDS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>7.1 Multi-Service Packages</strong></p>
                <p>For bundled packages (e.g., health checkup packages, nursing care sessions):</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Partial refunds available for unused services</li>
                  <li>Calculated on a pro-rata basis</li>
                  <li>Minimum utilization requirements may apply</li>
                </ul>

                <p><strong>7.2 Promotional Discounts</strong></p>
                <p>If you received a discount or used a promo code:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Refund will be for the amount actually paid</li>
                  <li>Promo codes may not be reissued once used</li>
                </ul>
              </div>
            </section>

            {/* Section 8 - Contact for Support */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. CONTACT US FOR REFUNDS & REPLACEMENTS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>For any questions, concerns, or requests related to cancellations, refunds, or replacements, please contact us:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-3">
                  <p><strong>Sanghmitra Healthcare Private Limited</strong></p>
                  <p><strong>Brand:</strong> HomeCure</p>
                  <p>📧 Email: Homecureexpress@gmail.com</p>
                  <p>📧 Support: support@homecure.com</p>
                  <p>📞 Phone: +91 98765 43210</p>
                  <p>💬 WhatsApp: +91 98765 43210</p>
                  <p>📍 Address: Patna, Bihar, India</p>
                  <p>🕒 Hours: 24/7 Support Available</p>
                  <p>🌐 Website: www.homecureexpress.com</p>
                </div>
                <p className="pt-3">We aim to respond to all refund and replacement requests within <strong>24-48 hours</strong>.</p>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
              <p className="text-sm text-gray-600 mt-3">
                Sanghmitra Healthcare Private Limited (HomeCure) reserves the right to update this Cancellations and Refunds Policy at any time. Changes will be communicated via email or website notification. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CancellationsAndRefunds;
